import { Entity, formatTicker } from '@api';
import { Button } from '@design/components/button/Button';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import clsx from 'clsx';
import React from 'react';

import styles from './EntityHeaderWatchlist.module.scss';

export interface EntityHeaderWatchlistProps {
  entity: Entity;
  logoSize?: number;
  className?: string;
  isFetching?: boolean;
}

export const EntityHeaderWatchlist = ({
  entity,
  logoSize = 40,
  className,
  isFetching,
}: EntityHeaderWatchlistProps) => {
  const ticker = formatTicker(entity);
  const entityName = entity.name_short || entity.name_full;

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '50px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  return (
    <div
      data-entity-header-watchlist
      className={clsx(styles.contentWrapper, className)}
    >
      <div className={styles.ticker}>
        <div className={styles.leftImageContainer}>
          <AssetLogoContainer size={logoSize} entity={entity} />
        </div>
        <div className={styles.titleContainer}>
          <div className={styles.nameRow}>
            <div className={styles.entityName}>
              <TooltipContainer label={entityName} placement="top">
                <span data-testid={`label${entityName}`}>{entityName}</span>
              </TooltipContainer>
            </div>
            <WatchlistTrigger
              tag={entity.tag}
              dropdownProps={{
                strategy: 'fixed',
                placement: 'top-start',
                inPortal: false,
              }}
              renderTrigger={isInWatchlist => (
                <Button
                  className={clsx(styles.watchlistButton, {
                    [styles.watchlistButtonActive]: isInWatchlist,
                  })}
                  data-testid="watchlist-button"
                  size="xsmall"
                  variant="tertiary"
                  iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                />
              )}
            />
          </div>
          <span className={styles.tag}>{ticker}</span>
        </div>
      </div>
    </div>
  );
};
